import qs from 'qs';
import { AxiosError } from 'axios';

import { curriculumGateway } from '../curriculum-gateway';

import { Country, LearningObjective, LearningObjectiveType } from '@types';

export interface LearningObjectiveParams {
    page?: number;
    per_page?: number;
    objective_type?: LearningObjectiveType;
    sort?: string;
    legacy?: LearningObjectiveType;
    country?: Country;
}

const fields = [
    'id',
    'title',
    'active',
    'objective_type',
    'year_id',
    'standard_codes',
    'slides_counter',
    'created_at',
    'updated_at',
    'ai',
    'learning_contents',
    'country',
];

const include = ['learning_contents'];

export const getLearningObjectives = async (
    params: LearningObjectiveParams = {},
): Promise<{ learningObjectives: LearningObjective[]; totalCount: number }> => {
    return curriculumGateway
        .get(`/learning_objectives`, {
            params: {
                ...params,
                fields: fields.join(','),
                include: include.join(','),
            },
            /**
             * passing array values will format as follows:
             * INPUT: { "year_id": [3, 4] }  OUTPUT: &year_id[]=3&year_id[]=4
             *
             * This works for integer but cannot consume strings:
             * INPUT: { "impact": ["low", "high"]}  OUTPUT: &impact[]=low&impact[]=high
             *
             * the below function formats all arrays as CVS
             * INPUT: { "impact": ["low", "high"]}  OUTPUT: &impact=low%2Chigh
             * which can be consumed successfully by the API
             */
            paramsSerializer: (params) => {
                const formattedParams = Object.keys(params).reduce(
                    (acc: { [key: string]: string }, key: string) => {
                        acc[key] = Array.isArray(params[key])
                            ? params[key].join(',')
                            : params[key];
                        return acc;
                    },
                    {},
                );
                return qs.stringify(formattedParams);
            },
            withCredentials: true,
        })
        .then((response) => {
            return {
                learningObjectives: response.data,
                totalCount: Number(response.headers.total),
            };
        })
        .catch(() => {
            throw new Error('Something went wrong loading the programmes.');
        });
};

export const getSingleLearningObjective = async (
    learningObjectiveId: number,
): Promise<LearningObjective> => {
    return curriculumGateway
        .get(`/learning_objectives/${learningObjectiveId}`, {
            withCredentials: true,
        })
        .then((response) => {
            return response.data;
        })
        .catch(() => {
            throw new Error(
                'Something went wrong loading the learning objective.',
            );
        });
};

export interface LearningObjectiveValues {
    country: Country;
    objective_type?: LearningObjectiveType;
    title?: string;
    year_id?: number;
    active?: boolean;
    learning_contents?: LearningContentsValues[];
    standard_codes?: string[];
    ai?: boolean;
}

export interface LearningContentsValues {
    id?: number;
    title?: string;
    step_number?: number;
    _destroy?: boolean;
}

export const createLearningObjectives = async (
    data: LearningObjectiveValues,
): Promise<LearningObjective> => {
    return curriculumGateway
        .post(`/learning_objectives`, data, {
            withCredentials: true,
        })
        .then((response) => {
            return response.data;
        })
        .catch((error: AxiosError) => {
            if (error.response?.status === 422) {
                throw new Error(
                    'A learning objective already exists with this title.',
                );
            }

            throw new Error('Something went creating the learning objective.');
        });
};

export const editLearningObjective = async (
    id: number,
    data: LearningObjectiveValues,
): Promise<LearningObjective> => {
    return curriculumGateway
        .put(`/learning_objectives/${id}`, data, {
            withCredentials: true,
        })
        .then((response) => response.data)
        .catch((error: AxiosError) => {
            if (error.response?.status === 422) {
                throw new Error(
                    'A learning objective already exists with this title.',
                );
            }

            throw new Error('Something went updating the learning objective.');
        });
};

export const deleteLearningObjective = async (
    id: number,
): Promise<LearningObjective> => {
    return curriculumGateway
        .delete(`/learning_objectives/${id}`, {
            withCredentials: true,
        })
        .then((response) => {
            return response.data;
        })
        .catch(() => {
            throw new Error(
                'Something went wrong deleting the learning objective.',
            );
        });
};
